import { Component, inject, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { OperatorGroupService } from '../../../../api/gen/api/operatorGroup.service';
import { ParkingOperatorDto } from '../../../../api/gen/model/parkingOperatorDto';
import { OperatorGroupMembershipDto } from '../../../../api/gen/model/operatorGroupMembershipDto';

export interface GroupMembershipWithSpace {
  membership: OperatorGroupMembershipDto;
  parkingSpaceName?: string;
}

export interface UnassignParkingSpaceDialogData {
  operator: ParkingOperatorDto;
}

@Component({
  selector: 'app-unassign-parking-space-dialog',
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatSelectModule,
    MatOptionModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
    MatCardModule,
    MatIconModule
  ],
  templateUrl: './unassign-parking-space-dialog.component.html',
  styleUrl: './unassign-parking-space-dialog.component.scss'
})
export class UnassignParkingSpaceDialogComponent {
  private dialogRef = inject(MatDialogRef<UnassignParkingSpaceDialogComponent>);
  private data = inject<UnassignParkingSpaceDialogData>(MAT_DIALOG_DATA);
  private operatorGroupService = inject(OperatorGroupService);
  private snackBar = inject(MatSnackBar);

  // Signals
  selectedMembershipId = signal<string>('');
  isUnassigning = signal(false);

  // Get operator from dialog data
  get operator(): ParkingOperatorDto {
    return this.data.operator;
  }

  // Get group memberships that are associated with parking spaces
  get parkingSpaceGroupMemberships(): GroupMembershipWithSpace[] {
    if (!this.operator.operatorGroupMemberships) {
      return [];
    }

    return this.operator.operatorGroupMemberships
      .filter(m => m.isActive && m.operatorGroup?.parkingSpaceOperators && m.operatorGroup.parkingSpaceOperators.length > 0)
      .map(m => {
        const parkingSpaces = m.operatorGroup?.parkingSpaceOperators?.map(pso => pso.parkingSpace?.name).filter(Boolean);
        return {
          membership: m,
          parkingSpaceName: parkingSpaces?.join(', ') || 'Unknown Space'
        };
      });
  }

  onUnassign(): void {
    const selectedMembershipId = this.selectedMembershipId();
    if (!selectedMembershipId || !this.operator.id) {
      return;
    }

    const membership = this.operator.operatorGroupMemberships?.find(m => m.id === selectedMembershipId);
    if (!membership || !membership.operatorGroupId) {
      this.snackBar.open('Invalid group membership selected', 'Close', { duration: 3000 });
      return;
    }

    this.isUnassigning.set(true);

    // Remove operator from the group
    this.operatorGroupService.operatorGroupsOperatorGroupIdOperatorsOperatorIdDelete(
      membership.operatorGroupId,
      this.operator.id
    ).subscribe({
      next: (result) => {
        if (result) {
          this.snackBar.open('Operator removed from parking space access group successfully', 'Close', { duration: 3000 });
          this.dialogRef.close(true); // Return true to indicate success
        }
      },
      error: (error) => {
        console.error('Error removing operator from group:', error);
        this.snackBar.open('Failed to remove operator from parking space group', 'Close', { duration: 5000 });
        this.isUnassigning.set(false);
      }
    });
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }

  getOperatorDisplayName(): string {
    const firstName = this.operator.firstName || '';
    const lastName = this.operator.lastName || '';
    const fullName = `${firstName} ${lastName}`.trim();
    return fullName || `Operator ${this.operator.id?.substring(0, 8)}` || 'Unknown Operator';
  }
}
